// ============================================
// P2 E-02：AI 助手状态（浮窗开关/会话/消息/流式）
// ============================================
import { create } from 'zustand';
import {
  chatAiOnce, chatAiStream, deleteAiSession, fetchAiMessages, fetchAiSessions, fetchAiStatus,
  type AiMessageItem, type AiRefItem, type AiSessionItem, type AiStatus,
} from '../api/ai';

interface AiState {
  status: AiStatus | null;
  open: boolean;
  sessions: AiSessionItem[];
  currentSessionId: string | null;
  messages: AiMessageItem[];
  sending: boolean;
  streamingText: string;
  error: string;
  quotaExceeded: boolean;
  model: string;

  init: () => Promise<void>;
  setOpen: (v: boolean) => void;
  setModel: (m: string) => void;
  newSession: () => void;
  selectSession: (id: string) => Promise<void>;
  removeSession: (id: string) => Promise<void>;
  send: (text: string) => Promise<void>;
  clearError: () => void;
}

let tempId = 0;
const localMsg = (role: 'user' | 'assistant', content: string, refs?: AiRefItem[]): AiMessageItem => ({
  id: `local-${++tempId}`,
  role,
  content,
  refs,
  createdAt: new Date().toISOString(),
});

export const useAiStore = create<AiState>((set, get) => ({
  status: null,
  open: false,
  sessions: [],
  currentSessionId: null,
  messages: [],
  sending: false,
  streamingText: '',
  error: '',
  quotaExceeded: false,
  model: '',

  init: async () => {
    try {
      const status = await fetchAiStatus();
      set({ status, model: status.model });
      if (status.enabled) {
        const { items } = await fetchAiSessions();
        set({ sessions: items });
      }
    } catch {
      set({ status: null });
    }
  },

  setOpen: (v) => set({ open: v }),
  setModel: (m) => set({ model: m }),
  clearError: () => set({ error: '', quotaExceeded: false }),

  newSession: () => set({ currentSessionId: null, messages: [], streamingText: '', error: '', quotaExceeded: false }),

  selectSession: async (id) => {
    set({ currentSessionId: id, streamingText: '', error: '' });
    try {
      const { items } = await fetchAiMessages(id);
      set({ messages: items });
    } catch (e: any) {
      set({ error: e?.response?.data?.message || e?.message || '加载会话失败' });
    }
  },

  removeSession: async (id) => {
    try {
      await deleteAiSession(id);
      const rest = get().sessions.filter((s) => s.id !== id);
      set({ sessions: rest, ...(get().currentSessionId === id ? { currentSessionId: null, messages: [] } : {}) });
    } catch (e: any) {
      set({ error: e?.response?.data?.message || e?.message || '删除会话失败' });
    }
  },

  send: async (text) => {
    const message = String(text || '').trim();
    if (!message || get().sending) return;
    set((s) => ({
      sending: true,
      error: '',
      quotaExceeded: false,
      streamingText: '',
      messages: [...s.messages, localMsg('user', message)],
    }));

    let sessionId = get().currentSessionId || undefined;
    let refs: AiRefItem[] = [];
    let streamed = '';

    const finalize = (content: string, err?: string) => {
      set((s) => ({
        sending: false,
        streamingText: '',
        messages: content
          ? [...s.messages, localMsg('assistant', content, refs)]
          : s.messages,
        error: err || '',
      }));
      if (sessionId) {
        fetchAiSessions().then(({ items }) => set({ sessions: items })).catch(() => {});
      }
    };

    try {
      await chatAiStream(
        { message, sessionId, model: get().model || undefined },
        (ev) => {
          if (ev.type === 'meta') {
            sessionId = ev.sessionId;
            refs = ev.refs || [];
            set({ currentSessionId: ev.sessionId });
          } else if (ev.type === 'delta') {
            streamed += ev.text;
            set({ streamingText: streamed });
          } else if (ev.type === 'error') {
            throw Object.assign(new Error(ev.message), { code: ev.code });
          }
        },
      );
      finalize(streamed);
    } catch (e: any) {
      // 流式失败降级为非流式（一次重试）；403/503 不降级
      const code = e?.code || e?.response?.status;
      if (!streamed && code !== 403 && code !== 503) {
        try {
          const r = await chatAiOnce({ message, sessionId, model: get().model || undefined });
          sessionId = r.sessionId;
          refs = r.refs || [];
          set({ currentSessionId: r.sessionId });
          finalize(r.content);
          return;
        } catch (e2: any) {
          finalize('', e2?.response?.data?.message || e2?.message || 'AI 对话失败');
          return;
        }
      }
      const msg = e?.response?.data?.message || e?.message || 'AI 对话失败';
      if (code === 403) set({ quotaExceeded: true });
      finalize(streamed, String(msg));
    }
  },
}));

export default useAiStore;
